import { apiClient } from './api';

// ==================== 타입 정의 ====================

export enum PermissionType {
  ALLOW = 'ALLOW',
  DENY = 'DENY',
}

export interface PagePermission {
  id: number;
  path: string;
  name: string;
  description?: string;
  allowedRoles: string[];
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeePagePermission {
  id: number;
  employeeId: number;
  path: string;
  permissionType: PermissionType;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeWithPermissions {
  id: number;
  empNo: string;
  name: string;
  email: string;
  role: string;
  position: string;
  department: string;
  individualPermissions: {
    id: number;
    path: string;
    permissionType: PermissionType;
  }[];
}

export interface PermissionCheckResult {
  hasAccess: boolean;
  reason: string;
}

// ==================== DTO ====================

export interface CreatePagePermissionDto {
  path: string;
  name: string;
  description?: string;
  allowedRoles: string[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdatePagePermissionDto {
  path?: string;
  name?: string;
  description?: string;
  allowedRoles?: string[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface CreateEmployeePagePermissionDto {
  employeeId: number;
  path: string;
  permissionType: PermissionType;
}

export interface UpdateEmployeePagePermissionDto {
  permissionType?: PermissionType;
}

export interface UpdateEmployeeRoleDto {
  role: string;
}

// ==================== API ====================

export const permissionApi = {
  // ==================== 페이지 권한 API ====================

  // 모든 페이지 권한 조회
  getAllPagePermissions: (): Promise<PagePermission[]> => {
    return apiClient.get<PagePermission[]>('/permissions/pages');
  },

  // 특정 페이지 권한 조회
  getPagePermissionById: (id: number): Promise<PagePermission> => {
    return apiClient.get<PagePermission>(`/permissions/pages/${id}`);
  },

  // 페이지 권한 생성
  createPagePermission: (dto: CreatePagePermissionDto): Promise<PagePermission> => {
    return apiClient.post<PagePermission>('/permissions/pages', dto);
  },

  // 페이지 권한 수정
  updatePagePermission: (id: number, dto: UpdatePagePermissionDto): Promise<PagePermission> => {
    return apiClient.patch<PagePermission>(`/permissions/pages/${id}`, dto);
  },

  // 페이지 권한 삭제
  deletePagePermission: (id: number): Promise<void> => {
    return apiClient.delete(`/permissions/pages/${id}`);
  },

  // 초기 데이터 시딩
  seedPagePermissions: (): Promise<{ created: number; skipped: number }> => {
    return apiClient.post<{ created: number; skipped: number }>('/permissions/pages/seed', {});
  },

  // ==================== 직원 개별 권한 API ====================

  // 모든 직원 개별 권한 조회
  getAllEmployeePermissions: (): Promise<EmployeePagePermission[]> => {
    return apiClient.get<EmployeePagePermission[]>('/permissions/employees');
  },

  // 직원별 개별 권한 조회
  getEmployeePermissions: (employeeId: number): Promise<EmployeePagePermission[]> => {
    return apiClient.get<EmployeePagePermission[]>(`/permissions/employees/${employeeId}`);
  },

  // 직원 개별 권한 생성/수정
  createEmployeePermission: (dto: CreateEmployeePagePermissionDto): Promise<EmployeePagePermission> => {
    return apiClient.post<EmployeePagePermission>('/permissions/employees/permission', dto);
  },

  // 직원 개별 권한 수정
  updateEmployeePermission: (id: number, dto: UpdateEmployeePagePermissionDto): Promise<EmployeePagePermission> => {
    return apiClient.patch<EmployeePagePermission>(`/permissions/employees/permission/${id}`, dto);
  },

  // 직원 개별 권한 삭제
  deleteEmployeePermission: (id: number): Promise<void> => {
    return apiClient.delete(`/permissions/employees/permission/${id}`);
  },

  // 직원의 특정 페이지 권한 삭제
  deleteEmployeePermissionByPath: (employeeId: number, path: string): Promise<void> => {
    return apiClient.delete(`/permissions/employees/${employeeId}/path?path=${encodeURIComponent(path)}`);
  },

  // ==================== 권한 체크 API ====================

  // 직원의 페이지 접근 권한 체크
  checkAccess: (employeeId: number, path: string): Promise<PermissionCheckResult> => {
    return apiClient.get<PermissionCheckResult>(`/permissions/check/${employeeId}?path=${encodeURIComponent(path)}`);
  },

  // 직원의 접근 가능한 페이지 목록
  getAccessiblePages: (employeeId: number): Promise<PagePermission[]> => {
    return apiClient.get<PagePermission[]>(`/permissions/accessible/${employeeId}`);
  },

  // ==================== 직원 권한 관리 API ====================

  // 직원 권한 변경
  updateEmployeeRole: (employeeId: number, role: string): Promise<void> => {
    return apiClient.patch(`/permissions/employees/${employeeId}/role`, { role });
  },

  // 권한 관리용 직원 목록 조회
  getEmployeesForPermission: (): Promise<EmployeeWithPermissions[]> => {
    return apiClient.get<EmployeeWithPermissions[]>('/permissions/employees-with-permissions');
  },
};
